/* eslint-disable no-unneeded-ternary */
/* eslint-disable no-underscore-dangle */
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import {
    Alert,
    Box,
    Button,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Typography,
    useTheme,
} from '@mui/material';
import { RefObject, useContext, useEffect, useRef, useState } from 'react';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import LargeMaterialUISwitch from '../../../components/common/LargeMaterialUISwitch';
import PageContainer from '../../../components/common/PageContainter';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import MessageUsersAutoMassDmBulkDeleteDialog from '../../../components/services/messageUsers/autoMassDm/MassageUsersAutoMassDmBulkDeleteDialog';
import MassageUsersAutoMassDmBulkAdjustDialog from '../../../components/services/messageUsers/autoMassDm/MassageUsersAutoMassDmBulkAdjustDialog';
import MassageUsersAutoMassDmBulkDistributeDialog from '../../../components/services/messageUsers/autoMassDm/MassageUsersAutoMassDmBulkDistributeDialog';
import MessageUsersAutoMassDmBulkPauseDialog from '../../../components/services/messageUsers/autoMassDm/MessageUsersAutoMassDmBulkPauseDialog';
import MessageUsersAutoMassDmScheduleSearchBar from '../../../components/services/messageUsers/autoMassDm/MassageUsersAutoMassDmScheduleSearchBar';
import MessageUsersAutoMassDmAddMassDm from '../../../components/services/messageUsers/autoMassDm/MessageUsersAutoMassDmAddMassDm';
import MessageUsersAutoMassDmCalendar from '../../../components/services/messageUsers/autoMassDm/MessageUsersAutoMassDmCalendar';
import MessageUsersAutoMassDmDescription from '../../../components/services/messageUsers/autoMassDm/MessageUsersAutoMassDmDescription';
import MessageUsersAutoMassDmListTableRow from '../../../components/services/messageUsers/autoMassDm/MessageUsersAutoMassDmListTableRow';
import MessageUsersAutoMassDmQueueList from '../../../components/services/messageUsers/autoMassDm/MessageUsersAutoMassDmQueueList';
import MessageUsersAutoMassDmSearchBar from '../../../components/services/messageUsers/autoMassDm/MessageUsersAutoMassDmSearchBar';
import SextforceCaptionsDialog from '../../../components/services/sextforce/SextforceCaptionsDialog';
import useMessageUsersMassDmList, {
    SextforceAutoMassDmMessageListSortBy,
    SextforceAutoMassDmMessageStatus,
} from '../../../hooks/useMessageUsersMassDmList';
import useMessageUsersMassDmScheduleList, { MassDmScheduleListSortBy } from '../../../hooks/useMessageUsersMassDmScheduleList';
import useSubscriber from '../../../hooks/useSubscriber';
import useSubscriberMassDmQueue from '../../../hooks/useSubscriberMassDmQueue';
import { SettingsContext } from '../../../store/SettingsContext';
import { titleCase } from '../../../utils/common';
import { getUpcomingMassDmsScheduleCount } from '../../../utils/massDm';

export const TablePaginationActions = (props: {
    count: number;
    limit: number;
    setLimit: (limit: number) => void;
    skip: number;
    setSkip: (skip: number) => void;
    tableTopRef: RefObject<HTMLDivElement>;
}) => {
    const { count, limit, setLimit, skip, setSkip, tableTopRef } = props;

    const handleNextButtonClick = () => {
        setSkip(skip + limit);

        if (tableTopRef && tableTopRef.current) {
            tableTopRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleBackButtonClick = () => {
        setSkip(skip - limit);

        if (tableTopRef && tableTopRef.current) {
            tableTopRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <Stack
            direction="row"
            spacing={2}
            alignItems={'center'}
            justifyContent={'flex-end'}
            width={'100%'}
            sx={{
                mt: 2,
            }}
        >
            <Box sx={{ flex: '1 1 auto' }} />
            <Typography variant="body2">Rows per page:</Typography>
            <Select
                variant="standard"
                value={limit}
                onChange={e => {
                    setLimit(parseInt(e.target.value as string, 10));
                    setSkip(0);
                }}
                size="small"
                sx={{ width: 75 }}
            >
                {[10, 20, 50, 100].map(option => (
                    <MenuItem key={option} value={option}>
                        {option}
                    </MenuItem>
                ))}
            </Select>
            <Typography variant="body2">
                {(skip + 1).toLocaleString()}-{Math.min(skip + limit, count).toLocaleString()} of {count.toLocaleString()}
            </Typography>
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={handleBackButtonClick} disabled={skip === 0}>
                        <NavigateBeforeIcon />
                    </IconButton>
                    <IconButton onClick={handleNextButtonClick} disabled={skip + limit >= count}>
                        <NavigateNextIcon />
                    </IconButton>
                </Box>
            </Box>
        </Stack>
    );
};

const MessageUsersAutoMassDm = () => {
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();

    const [serviceActive, setServiceActive] = useState<boolean>(false);
    const [sendSuccessNotification, setSendSuccessNotification] = useState<boolean>(false);

    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState<boolean>(false);
    const [showBulkDistributeDialog, setShowBulkDistributeDialog] = useState<boolean>(false);
    const [showBulkAdjustDialog, setShowBulkAdjustDialog] = useState<boolean>(false);
    const [showBulkPauseDialog, setShowBulkPauseDialog] = useState<boolean>(false);
    // const [active, setActive] = useState<boolean>(false);

    // Mass DMs List
    const [query, setQuery] = useState<string>('');
    const [colorId, setColorId] = useState<string | undefined>(undefined);
    const [status, setStatus] = useState<SextforceAutoMassDmMessageStatus>('sent');
    const [offset, setOffset] = useState<number>(0);
    const [limit, setLimit] = useState<number>(20);
    const [sortBy, setSortBy] = useState<SextforceAutoMassDmMessageListSortBy>('date');
    const {
        data: massDms,
        isFetching: isMassDmsLoading,
        refetch: refetchMassDms,
    } = useMessageUsersMassDmList(query, status, offset, limit, sortBy);
    const [showAddNewForm, setShowAddNewForm] = useState<boolean>(false);
    const [showCaptionsDialog, setShowCaptionsDialog] = useState<boolean>(false);
    const tableTopRef = useRef<HTMLDivElement>(null);

    const {
        data: subscriber,
        isLoading: isSubscriberLoading,
        setServiceRunning,
        setServiceRunningLoading,
        setServiceSettings,
    } = useSubscriber();

    // Mass DM Schedule
    const [massDmsScheduleQuery, setMassDmsScheduleQuery] = useState<string>('');
    const [massDmsScheduleQueryActive, setMassDmsScheduleQueryActive] = useState<boolean>(true);
    const [massDmsScheduleSoryBy, setMassDmsScheduleSortBy] = useState<MassDmScheduleListSortBy>('scheduleDate');

    const {
        data: scheduledMessages,
        isLoading: scheduledMessagesLoading,
        refetch: refetchMassDmsSchedule,
        bulkDistributeScheduledMessages,
        bulkDistributeScheduledMessagesLoading,
        bulkAdjustScheduledMessages,
        bulkAdjustScheduledMessagesLoading,
        bulkDeleteScheduledMessages,
        bulkDeleteScheduledMessagesLoading,
        setMassDmScheduleInactiveStatus,
        isUpdatingScheduledMessageStatus,
        bulkSetMassDmScheduleInactiveStatus,
        bulkSetMassDmScheduleInactiveStatusLoading,
        fetchActiveScheduledMessagesForBulkPause,
    } = useMessageUsersMassDmScheduleList(massDmsScheduleQuery, massDmsScheduleQueryActive, colorId, massDmsScheduleSoryBy);

    // OnlyFans Mass DM Queue
    const {
        data: massDmQueue,
        isLoading: isMassDmQueueLoading,
        refetch: refetchMassDmQueue,
    } = useSubscriberMassDmQueue(subscriber && '_id' in subscriber && subscriber.isLoggedIn ? true : false);

    useEffect(() => {
        if (subscriber && subscriber.sextforce) {
            if (subscriber.sextforce.autoMassDm) {
                setServiceActive(
                    'active' in subscriber.sextforce.autoMassDm && typeof subscriber.sextforce.autoMassDm.active === 'boolean'
                        ? subscriber.sextforce.autoMassDm.active
                        : false,
                );
                setSendSuccessNotification(
                    'sendSuccessNotification' in subscriber.sextforce.autoMassDm &&
                        typeof subscriber.sextforce.autoMassDm.sendSuccessNotification === 'boolean'
                        ? subscriber.sextforce.autoMassDm.sendSuccessNotification
                        : true,
                );
            } else {
                setServiceActive(true);
                setSendSuccessNotification(true);
            }
        } else {
            setServiceActive(false);
            setSendSuccessNotification(true);
        }
    }, [subscriber]);

    const upcomingMassDmsScheduleCount: number = scheduledMessages ? getUpcomingMassDmsScheduleCount(scheduledMessages) : 0;

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.messageUsers.homeUrl}`} />{' '}
                        Auto Mass DM for {subscriber.username}
                    </Typography>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Auto Mass DM" />
                    </StyledCard>

                    <StyledCard>
                        <MessageUsersAutoMassDmDescription />
                    </StyledCard>

                    <StyledCard>
                        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                            <Grid item xs>
                                Auto Mass DM is currently{' '}
                                {serviceActive ? (
                                    <span style={{ color: theme.palette.success.dark }}>running</span>
                                ) : (
                                    <span style={{ color: theme.palette.error.main }}>not running</span>
                                )}
                                .
                            </Grid>
                            <Grid item xs="auto">
                                {isSubscriberLoading || setServiceRunningLoading ? (
                                    <Skeleton width={'115px'} height={'51px'} />
                                ) : (
                                    <LargeMaterialUISwitch
                                        checked={serviceActive}
                                        onChange={e => {
                                            setServiceRunning('sextforce.autoMassDm', e.target.checked);
                                            setServiceActive(e.target.checked);
                                        }}
                                        color="primary"
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction={'row'} spacing={1} alignItems="center" justifyContent={'space-between'}>
                                    <Stack direction={'column'} spacing={1} alignItems="flex-start">
                                        <Typography variant="body1">Send notification on Telegram for successful message sent?</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            If enabled, the system will send a notification to your Telegram account when a Mass DM is
                                            successfully sent out. If you have a lot of Mass DMs scheduled, this can be annoying and you
                                            might prefer to turn these off. It will still send a notification if the Mass DM fails to send
                                            for any reason.
                                        </Typography>
                                    </Stack>
                                    {isSubscriberLoading || setServiceRunningLoading ? (
                                        <Skeleton width={'60px'} variant="text" />
                                    ) : (
                                        <Switch
                                            checked={sendSuccessNotification}
                                            size="medium"
                                            onChange={e => {
                                                setServiceSettings('sextforce.autoMassDm', {
                                                    active: serviceActive,
                                                    sendSuccessNotification: e.target.checked,
                                                });
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar
                            title={`Currently Sending Mass DMs (${massDmQueue ? massDmQueue.length.toLocaleString() : 0})`}
                            isLoading={isMassDmQueueLoading}
                        />
                    </StyledCard>
                    <StyledCard
                        sx={{
                            paddingLeft: 0,
                            paddingRight: 0,
                        }}
                    >
                        {!massDmQueue || massDmQueue.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                No Mass DMs currently sending
                            </Typography>
                        ) : null}
                        {massDmQueue && massDmQueue.length > 0 && (
                            <MessageUsersAutoMassDmQueueList
                                massDmQueue={massDmQueue}
                                refetchMassDmQueue={refetchMassDmQueue}
                                refetchMassDms={refetchMassDms}
                            />
                        )}
                    </StyledCard>

                    {!showAddNewForm && (
                        <StyledCard noCard>
                            <Button
                                variant="contained"
                                color="secondary"
                                fullWidth
                                size="large"
                                onClick={() => {
                                    setShowAddNewForm(true);
                                }}
                            >
                                Add New Mass DM
                            </Button>
                        </StyledCard>
                    )}

                    <StyledCard noCard>
                        <Button
                            variant="contained"
                            color="info"
                            fullWidth
                            size="large"
                            startIcon={<SubtitlesIcon />}
                            onClick={() => setShowCaptionsDialog(true)}
                        >
                            Manage Captions
                        </Button>
                    </StyledCard>
                    <SextforceCaptionsDialog open={showCaptionsDialog} onClose={() => setShowCaptionsDialog(false)} />

                    {showAddNewForm && (
                        <MessageUsersAutoMassDmAddMassDm
                            subscriber={subscriber}
                            refetchMassDms={refetchMassDms}
                            setShowForm={setShowAddNewForm}
                            handleCancel={() => {
                                setShowAddNewForm(false);
                            }}
                        />
                    )}

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar
                            title={`Scheduled Mass DMs (${scheduledMessages ? scheduledMessages.length.toLocaleString() : 0})`}
                            isLoading={scheduledMessagesLoading}
                        />
                    </StyledCard>
                    <MessageUsersAutoMassDmScheduleSearchBar
                        scheduledMessagesLoading={scheduledMessagesLoading}
                        queryScheduleActive={massDmsScheduleQueryActive}
                        setQueryScheduleActive={setMassDmsScheduleQueryActive}
                        setQuery={setMassDmsScheduleQuery}
                        sortBy={massDmsScheduleSoryBy}
                        setSortBy={setMassDmsScheduleSortBy}
                        colorId={colorId}
                        setColorId={setColorId}
                        theme={theme}
                    />

                    <StyledCard noCard noBottomMargin>
                        <Box
                            sx={{
                                bgcolor: 'grey.50',
                                p: 2,
                                pt: 0,
                                // Elevation 1
                                boxShadow: theme.shadows[1],
                                borderRadius: theme.shape.borderRadius,
                            }}
                        >
                            <Stack direction="column" spacing={2}>
                                {serviceActive && (
                                    <Alert severity={upcomingMassDmsScheduleCount < 50 ? 'success' : 'warning'}>
                                        {upcomingMassDmsScheduleCount.toLocaleString()} upcoming Mass DMs in the next 24 hours
                                    </Alert>
                                )}

                                <div>
                                    <Grid container spacing={1} alignItems="stretch">
                                        <Grid item xs={12} sm>
                                            <Button
                                                variant="contained"
                                                color="info"
                                                fullWidth
                                                size="large"
                                                sx={{ height: '100%' }}
                                                startIcon={<ManageHistoryIcon />}
                                                onClick={() => {
                                                    setShowBulkDistributeDialog(true);
                                                }}
                                            >
                                                Bulk Distribute
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm>
                                            <Button
                                                variant="contained"
                                                color="info"
                                                fullWidth
                                                size="large"
                                                sx={{ height: '100%' }}
                                                startIcon={<MoreTimeIcon />}
                                                onClick={() => {
                                                    setShowBulkAdjustDialog(true);
                                                }}
                                            >
                                                Bulk Adjust
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm>
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                                size="large"
                                                sx={{ height: '100%' }}
                                                startIcon={<PowerSettingsNewIcon />}
                                                onClick={() => {
                                                    setShowBulkPauseDialog(true);
                                                }}
                                            >
                                                Bulk Pause / Resume
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                fullWidth
                                                size="large"
                                                sx={{ height: '100%' }}
                                                startIcon={<DeleteSweepIcon />}
                                                onClick={() => {
                                                    setShowBulkDeleteDialog(true);
                                                }}
                                            >
                                                Bulk Delete
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </div>

                                <MessageUsersAutoMassDmBulkDeleteDialog
                                    active={serviceActive}
                                    scheduledMessages={scheduledMessages || []}
                                    refetchMassDmsSchedule={refetchMassDmsSchedule}
                                    bulkDeleteScheduledMessages={bulkDeleteScheduledMessages}
                                    bulkDeleteScheduledMessagesLoading={bulkDeleteScheduledMessagesLoading}
                                    open={showBulkDeleteDialog}
                                    setOpen={setShowBulkDeleteDialog}
                                />
                                <MassageUsersAutoMassDmBulkDistributeDialog
                                    scheduledMessages={scheduledMessages || []}
                                    refetchMassDmsSchedule={refetchMassDmsSchedule}
                                    bulkDistributeScheduledMessages={bulkDistributeScheduledMessages}
                                    bulkDistributeScheduledMessagesLoading={bulkDistributeScheduledMessagesLoading}
                                    open={showBulkDistributeDialog}
                                    setOpen={setShowBulkDistributeDialog}
                                />
                                <MassageUsersAutoMassDmBulkAdjustDialog
                                    scheduledMessages={scheduledMessages || []}
                                    refetchMassDmsSchedule={refetchMassDmsSchedule}
                                    bulkAdjustScheduledMessages={bulkAdjustScheduledMessages}
                                    bulkAdjustScheduledMessagesLoading={bulkAdjustScheduledMessagesLoading}
                                    open={showBulkAdjustDialog}
                                    setOpen={setShowBulkAdjustDialog}
                                />
                                <MessageUsersAutoMassDmBulkPauseDialog
                                    fetchActiveScheduledMessages={fetchActiveScheduledMessagesForBulkPause}
                                    refetchMassDmsSchedule={refetchMassDmsSchedule}
                                    bulkSetMassDmScheduleInactiveStatus={bulkSetMassDmScheduleInactiveStatus}
                                    bulkSetMassDmScheduleInactiveStatusLoading={bulkSetMassDmScheduleInactiveStatusLoading}
                                    open={showBulkPauseDialog}
                                    setOpen={setShowBulkPauseDialog}
                                />
                            </Stack>
                        </Box>
                    </StyledCard>
                    <StyledCard noCard>
                        <MessageUsersAutoMassDmCalendar
                            subscriber={subscriber}
                            scheduledMessages={scheduledMessages}
                            scheduledMessagesLoading={scheduledMessagesLoading}
                            refetchMassDmsSchedule={refetchMassDmsSchedule}
                            refetchMassDms={refetchMassDms}
                            setMassDmScheduleInactiveStatus={setMassDmScheduleInactiveStatus}
                            isUpdatingScheduledMessageStatus={isUpdatingScheduledMessageStatus}
                        />
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar
                            title={`OnlyFans Mass DMs - ${titleCase(status)} (${
                                massDms && massDms.total ? massDms.total.toLocaleString() : 0
                            })`}
                            isLoading={isMassDmsLoading}
                        />
                    </StyledCard>

                    <MessageUsersAutoMassDmSearchBar
                        massDmsLoading={isMassDmsLoading}
                        status={status}
                        setStatus={newStatus => {
                            if (newStatus !== status) {
                                setStatus(newStatus);
                                setOffset(0);
                            }
                        }}
                        setQuery={setQuery}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        theme={theme}
                    />
                    <StyledCard>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Here you can see the list of Mass DMs that have been sent, scheduled, and unsent, just like it appears on
                            OnlyFans.
                        </Typography>

                        <Alert severity="info" icon={false} sx={{ mb: 1 }}>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={12}>
                                    <Stack direction="column" spacing={0}>
                                        <Typography variant="body2" fontWeight="bold">
                                            Actions
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                            <DeleteIcon fontSize="small" color="error" />
                                            <Typography variant="body2">Unsend Mass DM</Typography>
                                        </Stack>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Alert>

                        {massDms && massDms.list && massDms.list.length === 0 && (
                            <Typography
                                variant="body1"
                                sx={{
                                    textAlign: 'center',
                                    color: theme.palette.text.secondary,
                                    mt: 1,
                                }}
                            >
                                No Mass DMs found
                                <br />
                                Click on the button above to create a new Mass DM
                            </Typography>
                        )}
                        {massDms && massDms.list.length > 0 && (
                            <>
                                <Box
                                    ref={tableTopRef}
                                    sx={{
                                        display: 'block',
                                        overflowX: 'auto',
                                    }}
                                >
                                    <Table
                                        size="small"
                                        sx={{
                                            tableLayout: 'fixed',
                                            width: '100%',
                                            // No padding on the left and right of the table but only on the first and last cell
                                            '& td': {
                                                '&:first-of-type': {
                                                    paddingLeft: '0px',
                                                    paddingRight: '0px',
                                                },
                                                '&:last-child': {
                                                    paddingRight: '0px',
                                                },
                                                paddingLeft: 0.5,
                                                paddingRight: 0.5,
                                            },
                                            '& th': {
                                                '&:first-of-type': {
                                                    paddingLeft: '0px',
                                                    paddingRight: '0px',
                                                },
                                                '&:last-child': {
                                                    paddingRight: '0px',
                                                },
                                                // Make header cells bold
                                                fontWeight: 'bold',
                                                paddingLeft: 0.5,
                                                paddingRight: 0.5,
                                            },
                                        }}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ width: 120 }}>Date</TableCell>
                                                <TableCell>Name</TableCell>
                                                <TableCell sx={{ width: 70 }} align="center">
                                                    Sent
                                                </TableCell>
                                                <TableCell sx={{ width: 70 }} align="center">
                                                    Viewed
                                                </TableCell>
                                                <TableCell sx={{ width: 100 }} align="center">
                                                    Purchased
                                                </TableCell>
                                                <TableCell sx={{ width: 80 }} align="center">
                                                    Actions
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {massDms.list.map(massDm => (
                                                <MessageUsersAutoMassDmListTableRow
                                                    subscriber={subscriber}
                                                    massDm={massDm}
                                                    refetchMassDms={refetchMassDms}
                                                    key={massDm._id}
                                                />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                                <TablePaginationActions
                                    count={massDms.total}
                                    limit={limit}
                                    setLimit={setLimit}
                                    skip={offset}
                                    setSkip={setOffset}
                                    tableTopRef={tableTopRef}
                                />
                            </>
                        )}
                    </StyledCard>
                </>
            )}
        </PageContainer>
    );
};

export default MessageUsersAutoMassDm;
